import { Ok, Err } from '@cian/core/result/shared/index.js';
import { intoIterable } from '@cian/core/iterator/index.js';
import { deriveRestoreFn, deriveSendFn } from '@cian/core/threads/index.js';
import { Option } from '@cian/core/option/index.js';

/* oxlint-disable max-lines */
const NAMESPACE = 'config.filter';
const MATCHER_MARKER = NAMESPACE + '.Matcher';
const FILTER_LIST_SPLIT_SYM = ',';
const FILTER_LIST_KV_SPLIT_SYM = '=';
var UnreleasedFilters;
(function (UnreleasedFilters) {
    // TODO: Не реализован
    UnreleasedFilters["Probability"] = "probability";
})(UnreleasedFilters || (UnreleasedFilters = {}));
var SharedFilters;
(function (SharedFilters) {
    SharedFilters["Application"] = "application";
    SharedFilters["Url"] = "url";
    SharedFilters["UrlPath"] = "url-path";
    SharedFilters["Email"] = "email";
    SharedFilters["Context"] = "context";
    SharedFilters["McsRunEnv"] = "mcs_run_env";
    SharedFilters["InBrowser"] = "in_browser";
})(SharedFilters || (SharedFilters = {}));
/**
 * Фильтры, которые используются только внутри Frontend SDK
 */
var SDKFilters;
(function (SDKFilters) {
    /**
     * Фильтр используется микрофронтами. На странице может быть несколько разных экземпляров
     * одного микрофронта со своим дампом настроек, какие-то настройки можно склеить (например,
     * `application`), а какие-то наоборот, должны сохраниться, так как они имеют уникальное
     * значение для конкретного запроса (например, `initialState`). Если фильтр установлен в
     * `true`, то данная настройка не будет слита и в нее будут добавлены дополнительные фильтры
     * уникальности (например {@linkcode InstanceId})
     */
    SDKFilters["UniqueByRequest"] = "unique_by_request";
    /** @deprecated {@link InstanceId} */
    SDKFilters["RequestId"] = "request-id";
    /**
     * Фильтр используется тестами. В рамках прогона теста могут создаваться тестовые настройки,
     * которые должны удалиться после окончания прогона. Все такие настройки привязываются к id
     * сессии (прогона)
     */
    SDKFilters["SessionId"] = "session-id";
    /**
     * Фильтр используется микрофронтами. На странице может быть несколько разных экземпляров
     * одного микрофронта, у каждого экземпляра может быть свой набор уникальных настроек, чтобы
     * они не пересекались, используется фильтр
     */
    SDKFilters["InstanceId"] = "instance-id";
    /**
     * Тип окружения, в котором запущен MCS. Значение принимает одно из значений {@link McsRunEnvType},
     * по сути тоже самое, что и {@link SharedFilters.McsRunEnv}, но используется только внутри SDK и
     * его невозможно конфигурировать из Runtime Settings
     */
    SDKFilters["McsRunEnvType"] = "@mcs_run_env_type";
})(SDKFilters || (SDKFilters = {}));
function applicationFilter(application) {
    return new RegExpFilter(SharedFilters.Application, application);
}
function urlFilter() {
    return new RegExpFilter(SharedFilters.Url, function takeUrlFromContext(context) {
        return Option.undefinedToOption(context.url);
    });
}
function urlPathFilter() {
    return new RegExpFilter(SharedFilters.UrlPath, function takeUrlPathFromContext(context) {
        return Option.undefinedToOption(context.path);
    });
}
function emailFilter() {
    return new RegExpFilter(SharedFilters.Email, function takeEmailFromContext(context) {
        return Option.undefinedToOption(context.email);
    });
}
function contextFilter() {
    return new ListFilter(SharedFilters.Context);
}
function mcsRunEnvFilter(mcsRunEnv) {
    return new RegExpFilter(SharedFilters.McsRunEnv, mcsRunEnv);
}
function requestIdFilter() {
    return new StrictEqualFilter(SDKFilters.RequestId, function takeRequestIdFromContext(context) {
        return Option.undefinedToOption(context.requestId);
    });
}
function sessionIdFilter() {
    return new StrictEqualFilter(SDKFilters.SessionId, function takeSessionIdFromContext(context) {
        return Option.undefinedToOption(context.sessionId);
    });
}
function instanceIdFilter() {
    return new StrictEqualFilter(SDKFilters.InstanceId, function takeInstanceIdFromContext(context) {
        return Option.undefinedToOption(context.instanceId);
    });
}
function mcsRunEnvTypeFilter(mcsRunEnvType) {
    return new RegExpFilter(SDKFilters.McsRunEnvType, mcsRunEnvType);
}
/**
 * Набор базовых фильтров, которым не требуются дополнительные данные для инициализация
 *
 * **Внимание:** здесь отсутствуют фильтры {@linkcode SharedFilters.Application} и {@linkcode SharedFilters.McsRunEnv}
 *
 * @export
 * @return {IFilter<SharedFilters | SDKFilters>[]}
 */
function defaultFilters() {
    return [
        urlFilter(),
        urlPathFilter(),
        emailFilter(),
        okFilter(SDKFilters.UniqueByRequest),
        okFilter(SharedFilters.InBrowser),
        requestIdFilter(),
        sessionIdFilter(),
        instanceIdFilter(),
        contextFilter(),
    ];
}
/**
 * Класс `StrictEqualFilter` предназначен для выполнения строгого сравнения.
 * Этот класс используется для сравнения двух значений на строгое равенство.
 *
 * @example
 * const filter = new StrictEqualFilter('test', 'test');
 * const result = filter.match('test'); // true
 *
 * @export
 * @class StrictEqualFilter
 * @implements {IFilter<F>}
 * @template F - имя фильтра
 */
class StrictEqualFilter {
    static compare(a, b) {
        return a === b;
    }
    name;
    actual;
    constructor(name, actual) {
        this.name = name;
        this.actual = actual;
    }
    /**
     * Выполняет сравнение критерия и фактического значения.
     *
     * @param {FilterValue} criteria - Критерий, с которым будет сравниваться фактическое значение.
     * @param {IContext} [context] - Контекст, который может быть использован, если фактическое значение является функцией.
     * @return {boolean} Возвращает `true`, если критерий и фактическое значение строго равны, иначе `false`.
     * @memberof StrictEqualFilter
     */
    match(criteria, context) {
        if (typeof this.actual === 'function') {
            if (!context) {
                return false;
            }
            return this.actual(context).isSomeAnd(actual => actual === criteria);
        }
        return this.actual === criteria;
    }
}
class RegExpFilter {
    static compare(a, b) {
        return matchFilterValue(a, b);
    }
    name;
    actual;
    constructor(name, actual) {
        this.name = name;
        this.actual = actual;
    }
    match(criteria, context) {
        let actual;
        if (typeof this.actual === 'function') {
            if (!context) {
                return false;
            }
            const result = this.actual(context);
            if (result.isNone()) {
                return false;
            }
            actual = result.Some;
        }
        else {
            actual = this.actual;
        }
        return matchFilterValue(criteria, actual);
    }
}
class ListFilter {
    static compare(a, b) {
        return a === b;
    }
    name;
    /**
     * В ином случае, в качестве актуальных значений будет использован {@linkcode IContext.context}
     *
     * @private
     * @type {Record<string, ListFilterValue> | TakeListValueFromContext}
     * @memberof ListFilter
     */
    actual;
    keysFormatter;
    constructor(name, actual, keysFormatter) {
        this.name = name;
        this.actual = actual;
        this.keysFormatter = keysFormatter;
    }
    match(criteria, context) {
        let collection;
        if (typeof criteria !== 'string') {
            return false;
        }
        if (!this.actual) {
            if (context && context?.context) {
                collection = context.context;
            }
            else {
                return false;
            }
        }
        else {
            if (typeof this.actual === 'function') {
                if (!context) {
                    return false;
                }
                const result = this.actual(context);
                if (result.isNone()) {
                    return false;
                }
                collection = result.Some;
            }
            else {
                collection = this.actual;
            }
        }
        for (const parseResult of parseFilterExpression(criteria)) {
            if (parseResult.isErr()) {
                return false;
            }
            const { key, value: pattern } = parseResult.Ok;
            const value = collection[typeof this.keysFormatter === 'function' ? this.keysFormatter(key) : key];
            if (!value) {
                return false;
            }
            if (!matchFilterValue(pattern, value)) {
                return false;
            }
        }
        return true;
    }
}
function okFilter(name) {
    return {
        name,
        match: () => true,
    };
}
function falsyFilter(name) {
    return {
        name,
        match: () => false,
    };
}
function formatFilterValue(value) {
    if (Array.isArray(value)) {
        return value.join(',');
    }
    return typeof value === 'string' ? value : String(value);
}
function matchFilterValue(criteria, actual) {
    const pattern = formatFilterValue(criteria);
    const value = formatFilterValue(actual);
    const reg = new RegExp(pattern);
    return reg.test(value);
}
/**
 * Прасит выражения типа `IfA=Foo,IfB=Bar`
 *
 * @export
 * @param {string} expr - выражение `IfA=Foo,IfB=Bar`
 * @param {(string | string[])} [kvSep=FILTER_LIST_KV_SPLIT_SYM]
 * @return {IterableIterator<Result<ParseFilterExpressionResult, ErrFilterExpressionResult>>}
 */
function* parseFilterExpression(expr, kvSep = FILTER_LIST_KV_SPLIT_SYM) {
    const conditions = expr.split(FILTER_LIST_SPLIT_SYM);
    for (const condition of conditions) {
        if (Array.isArray(kvSep)) {
            let finded = false;
            for (const separator of kvSep) {
                const { key, value } = parseCondition(condition, separator);
                if (key && value) {
                    yield Ok({ condition, key, value, separator });
                    finded = true;
                    break;
                }
            }
            if (!finded) {
                yield Err({ condition });
            }
        }
        else {
            const { key, value } = parseCondition(condition, kvSep);
            if (key && value) {
                yield Ok({ condition, key, value, separator: kvSep });
            }
            else {
                yield Err({ condition });
            }
        }
    }
}
function parseCondition(condition, separator) {
    const [key, value] = condition.split(separator);
    return { key, value };
}
/**
 *
 * @param {ISetting} setting - настройка
 * @returns {boolean}
 */
function hasInBrowserFilter(setting) {
    return setting.filter[SharedFilters.InBrowser] === '1' || setting.filter[SharedFilters.InBrowser] === true;
}
/**
 * Создает функцию фильтрации настройки {@linkcode Matcher} из списка {@linkcode IFilter}
 *
 * @export
 * @template F
 * @param {IFilter<F>[]} filters
 * @return {SendableMatcher}
 */
function createMatcher(filters, name) {
    const filtersMap = new Map(filters.map(f => [f.name, f]));
    const sendable = deriveSendFn(function matcher(setting, context) {
        const { filter: settingFilters } = setting;
        if (!settingFilters) {
            return true;
        }
        // `as Required<ISetting['filter']>` выполняется, потому что иначе компилятор считает, что
        // итераторм может вернуть `undefined`, что не соответствует действительности
        for (const [key, criteria] of intoIterable(settingFilters)) {
            const filter = filtersMap.get(key);
            if (!filter) {
                return false;
            }
            if (!filter.match(criteria, context)) {
                return false;
            }
        }
        return true;
    }, name).unwrap();
    return sendable;
}
/**
 * Создает {@linkcode Matcher} из базового набора фильтров {@linkcode defaultFilters}
 *
 * @export
 * @return {ReturnType<typeof createMatcher}
 */
const defaultMatcher = createMatcher(defaultFilters(), MATCHER_MARKER);
defaultMatcher.restore = deriveRestoreFn(defaultMatcher);

export { FILTER_LIST_KV_SPLIT_SYM, FILTER_LIST_SPLIT_SYM, ListFilter, RegExpFilter, SDKFilters, SharedFilters, StrictEqualFilter, UnreleasedFilters, applicationFilter, contextFilter, createMatcher, defaultFilters, defaultMatcher, emailFilter, falsyFilter, hasInBrowserFilter, instanceIdFilter, matchFilterValue, mcsRunEnvFilter, mcsRunEnvTypeFilter, okFilter, parseFilterExpression, requestIdFilter, sessionIdFilter, urlFilter, urlPathFilter };
//# sourceMappingURL=filter.js.map
